import { useState, useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSacredAdmin } from "@/hooks/useSacredAdmin";
import {
  fetchJourneyDebug,
  adminJourneyRecompute,
} from "@/lib/sacred-journey/sacred-journey-api";
import { DailyGuidance } from "./DailyGuidance";
import { Loader2, ArrowLeft, RefreshCw } from "lucide-react";
import { getEditorAuth } from "@/lib/sacred-library/sacred-library-api";

export function JourneyTestPage() {
  const { isAdmin } = useSacredAdmin();
  const [email, setEmail] = useState("user@example.com");
  const [debug, setDebug] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const load = async () => {
    setLoading(true);
    setMessage("");
    try {
      const data = await fetchJourneyDebug(email.trim() || undefined);
      setDebug(data);
      if (data.error) setMessage("User not found");
    } catch (e) {
      setDebug(null);
      setMessage(e?.response?.data?.detail || "Load failed — admin login required");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isAdmin && getEditorAuth().token) load();
  }, [isAdmin]);

  const recompute = async () => {
    try {
      await adminJourneyRecompute(email.trim());
      setMessage("Recomputed");
      load();
    } catch (e) {
      setMessage(e?.response?.data?.detail || "Recompute failed");
    }
  };

  if (!getEditorAuth().token) {
    return (
      <div className="min-h-screen py-24 px-4">
        <Link to="/admin/sacred" className="text-primary underline">
          Sign in
        </Link>{" "}
        as admin first.
      </div>
    );
  }

  if (!isAdmin) return <Navigate to="/admin/sacred" replace />;

  return (
    <div className="min-h-screen py-24 px-4" data-testid="journey-test-page">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/admin/sacred">
              <ArrowLeft className="w-4 h-4 mr-1" /> Admin
            </Link>
          </Button>
          <h1 className="font-heading text-2xl">Journey Test</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Console:{" "}
          <code className="text-xs bg-muted px-1 rounded">testJourney(&quot;{email}&quot;)</code>
        </p>

        <Card>
          <CardContent className="pt-6 flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[220px]">
              <Label>User email</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com" />
            </div>
            <Button type="button" onClick={load} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Reload
            </Button>
            <Button type="button" variant="secondary" onClick={recompute}>
              Recompute
            </Button>
          </CardContent>
        </Card>

        {message && <p className="text-sm text-muted-foreground">{message}</p>}

        {debug?.computed_outputs && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Computed outputs</CardTitle>
            </CardHeader>
            <CardContent className="text-xs space-y-1">
              <p>Archetype: {debug.computed_outputs.archetype?.name}</p>
              <p>Gate: {debug.computed_outputs.karmic_gate?.name}</p>
              <p>Adhikāra: {debug.computed_outputs.adhikara_level?.name}</p>
              <p>Pathway: {debug.computed_outputs.pathway?.name}</p>
            </CardContent>
          </Card>
        )}

        {debug?.computed_outputs?.daily_guidance && (
          <DailyGuidance
            guidance={debug.computed_outputs.daily_guidance}
            date={debug.date}
          />
        )}

        {debug?.mapping_inputs && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Mapping inputs</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="text-xs overflow-auto max-h-64 bg-muted p-3 rounded">
                {JSON.stringify(debug.mapping_inputs, null, 2)}
              </pre>
            </CardContent>
          </Card>
        )}

        {debug?.raw_journey && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Raw journey KV</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="text-xs overflow-auto max-h-96 bg-muted p-3 rounded">
                {JSON.stringify(debug.raw_journey, null, 2)}
              </pre>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
